import { useEffect, useCallback } from 'react'
import { type Moment } from 'moment'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { useFilterParamsContext } from 'domains/insights-portal/utils/filter-params/filterParamsContext'
import {
  type GetInsightsPortalGroupComparisonQueryVariables,
  type GetInsightsPortalGroupMetricsQueryVariables,
  InsightsPortalSchema,
  InsightsPortalGroupComparisonRequestJoinBy,
  useGetInsightsPortalFilterOptionsLazyQuery,
  useGetInsightsPortalGroupComparisonQuery,
  useGetInsightsPortalGroupMetricsQuery,
  useGetInsightsPortalMetricsQuery,
  InsightsPortalOwner,
} from 'domains/insights-portal/api'
import { type FilterParamsState } from 'domains/insights-portal/utils/filter-params/filterParams.types'
import { OrderItemsMetrics } from 'domains/insights-portal/schemas/order-items/insightsPortalOrderItems.types'
import {
  type BaseDynamicFilterOptions,
  transformApiOptionsIntoDashboardOptions,
} from 'domains/insights-portal/schemas/utils/insightsPortalSchemas.utils'
import { useFormatValue } from 'domains/insights-portal/utils/format-value.hooks'
import { useInsightsPortalAuthorizationContext } from 'domains/insights-portal/utils/authorization-context.hooks'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { calculateComparisonPeriod } from 'domains/insights-portal/utils/insights-portal.utils'
import { type ProductByCategoryBenchmarkMetrics } from 'domains/insights-portal/schemas/product-by-category-benchmark/insightsPortalProductByCategoryBenchmark.types'
import { type ProductByPartnerBenchmarkMetrics } from 'domains/insights-portal/schemas/product-by-partner-benchmark/insightsPortalProductByPartnerBenchmark.types'
import {
  transformFilterSelectionsToOrderItemsSchemaFilters,
  transformOrderItemsApiOptionsConfig,
} from 'domains/insights-portal/schemas/order-items/insightsPortalOrderItemsSchema.helpers'
import { useInsightsPortalQueryTags } from 'domains/insights-portal/utils/query-tags.hooks'
import { useSchema } from 'domains/insights-portal/utils/schema.hooks'

// Convert the api filter names to dashboard filter names
export const mapApiNameToDashboardName = {
  WAREHOUSE: 'retailer',
  WAREHOUSE_LOCATION: 'storeLocation',
  WAREHOUSE_LOCATION_CODE: 'storeLocationCode',
  WAREHOUSE_LOCATION_GROUP: 'storeGroup',
  ORDER_SOURCE: 'orderSource',
  REGION: 'region',
  WAREHOUSE_LOCATION_STATE: 'state',
  PICKUP: 'fulfillmentType',
  ALCOHOL_DELIVERY: 'alcohol',
  L1_CATEGORY: 'L1Category',
  L2_CATEGORY: 'L2Category',
  L3_CATEGORY: 'L3Category',
  L4_CATEGORY: 'L4Category',
  L5_CATEGORY: 'L5Category',
  L6_CATEGORY: 'L6Category',
  EXPRESS: 'express',
  VIRTUAL_WAREHOUSE: 'convenience',
  LOYALTY: 'loyalty',
  EBT_DELIVERY: 'ebt',
  FIRST_ORDER: 'firstOrder',
  PARTNER_GEO_DIVISION: 'partnerDivision',
  ORDER_CURRENCY: 'orderCurrency',
}

export const useOrderItemsFilters = () => {
  const [getFilters, { data, loading, error }] = useGetInsightsPortalFilterOptionsLazyQuery()

  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const schema = useSchema(InsightsPortalSchema.OrderItem)

  const queryTags = useInsightsPortalQueryTags()

  useEffect(() => {
    getFilters({
      variables: {
        schema,
        partnerIds,
        retailerIds,
        retailerLocationIds,
        filters: transformFilterSelectionsToOrderItemsSchemaFilters(selectedFiltersValues, dates),
        owner: InsightsPortalOwner.RetailerTools,
        queryTags,
      },
    })
  }, [
    selectedFiltersValues,
    getFilters,
    dates,
    partnerIds,
    retailerIds,
    retailerLocationIds,
    schema,
    queryTags,
  ])

  const apiFilterOptions = transformApiOptionsIntoDashboardOptions(
    data?.insightsPortalServiceGetInsightsPortalFilterOptions?.filterOptions,
    transformOrderItemsApiOptionsConfig
  )

  const dashboardFilterOptions = Object.entries(apiFilterOptions).reduce(
    (dashboardFilterOptions, [apiFilterName, filterOptions]) => {
      dashboardFilterOptions[mapApiNameToDashboardName[apiFilterName]] = filterOptions

      return dashboardFilterOptions
    },
    {} as BaseDynamicFilterOptions
  )

  return {
    filterOptions: dashboardFilterOptions,
    loading,
    error,
  }
}

const useOrderItemsMetrics = (
  selectedFiltersValues: FilterParamsState['selectedFiltersValues'],
  dates: { from: Moment; to: Moment },
  metrics: OrderItemsMetrics[],
  filtersOverride?: Partial<FilterParamsState['selectedFiltersValues']>,
  skip?: boolean
) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }

  const filters = { ...selectedFiltersValues, ...filtersOverride }

  const schema = useSchema(InsightsPortalSchema.OrderItem)

  const { data, ...rest } = useGetInsightsPortalMetricsQuery({
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      metrics,
      filters: transformFilterSelectionsToOrderItemsSchemaFilters(filters, currentPeriodDateRange),
    },
    skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalMetricValues?.metricValues || [],
    ...rest,
  }
}

export const useCurrentPeriodOrderItemsMetrics = (
  metrics: OrderItemsMetrics[],
  filtersOverride?: Partial<FilterParamsState['selectedFiltersValues']>,
  skip?: boolean
) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  return useOrderItemsMetrics(
    selectedFiltersValues,
    currentPeriodDateRange,
    metrics,
    filtersOverride,
    skip
  )
}

export const useLastPeriodOrderItemsMetrics = (
  metrics: OrderItemsMetrics[],
  filtersOverride?: Partial<FilterParamsState['selectedFiltersValues']>,
  skip?: boolean
) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const lastPeriodDateRange = calculateComparisonPeriod(
    from,
    to,
    filtersOverride?.comparisonType || selectedFiltersValues.comparisonType
  )

  return useOrderItemsMetrics(
    selectedFiltersValues,
    lastPeriodDateRange,
    metrics,
    filtersOverride,
    skip
  )
}

export const useOrderItemMetricValueFormatter = () => {
  const { formatCurrency, formatInteger, formatPercentage } = useFormatValue()

  const formatter = useCallback(
    (metric: OrderItemsMetrics, value: number) => {
      const formatters = [
        { metric: OrderItemsMetrics.AVG_ITEM_FOUND, formatter: formatPercentage, compact: false },
        { metric: OrderItemsMetrics.AVG_ITEM_FILLED, formatter: formatPercentage, compact: false },
        { metric: OrderItemsMetrics.LOST_GMV, formatter: formatCurrency, compact: false },
        { metric: OrderItemsMetrics.AVG_BASKET, formatter: formatCurrency, compact: false },
        { metric: OrderItemsMetrics.AVG_ORDERED_ITEM, formatter: formatInteger, compact: false },
        {
          metric: OrderItemsMetrics.AVG_ITEM_FILLED_PRICE,
          formatter: formatCurrency,
          compact: false,
        },
        { metric: OrderItemsMetrics.COUNT_ORDER, formatter: formatInteger, compact: false },
        { metric: OrderItemsMetrics.SUM_GMV, formatter: formatCurrency, compact: false },
      ]

      const metricFormatter = formatters.find(f => f.metric === metric)

      if (metricFormatter) {
        return metricFormatter.formatter(value, metricFormatter.compact)
      }
      return value.toLocaleString()
    },
    [formatCurrency, formatInteger, formatPercentage]
  )

  return {
    formatter,
  }
}

type UseOrderItemsGroupMetricArgs = {
  metric: OrderItemsMetrics
  groupBy: string
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  skip?: boolean
  secondaryMetrics?: OrderItemsMetrics[]
  widget: string
} & Pick<GetInsightsPortalGroupMetricsQueryVariables, 'orderDirection' | 'limit' | 'offset'>

export const useOrderItemsGroupMetric = ({
  metric,
  groupBy,
  filtersObjOverride = null,
  skip = false,
  limit,
  offset,
  orderDirection,
  secondaryMetrics,
  widget,
}: UseOrderItemsGroupMetricArgs) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const schema = useSchema(InsightsPortalSchema.OrderItem)

  const queryTags = useInsightsPortalQueryTags({ widget })

  const { data, ...rest } = useGetInsightsPortalGroupMetricsQuery({
    skip: skip,
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      primaryMetric: metric,
      groupBy,
      limit,
      offset,
      orderDirection,
      secondaryMetrics,
      filters: transformFilterSelectionsToOrderItemsSchemaFilters(
        filterValues,
        currentPeriodDateRange
      ),
      owner: InsightsPortalOwner.RetailerTools,
      queryTags,
    },
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupMetricValue?.values || [],
    ...rest,
  }
}

type UseCompareGroupedPeriodsForOrderItems = {
  // TODO: this should probably be more strongly typed to Order Item specific metrics
  metric: string
  groupBy: string
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  skip?: boolean
  primarySecondaryMetrics?: OrderItemsMetrics[]
  comparisonSecondaryMetrics?: Array<
    ProductByCategoryBenchmarkMetrics | ProductByPartnerBenchmarkMetrics
  >
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

// TODO: Reduce Duplicated Code between this and Order Delivery Schema version of this hook
export const useCompareGroupedPeriodsForOrderItems = ({
  metric,
  groupBy,
  orderBy,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  skip = false,
  primarySecondaryMetrics,
  comparisonSecondaryMetrics,
}: UseCompareGroupedPeriodsForOrderItems) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const lastPeriodDateRange = calculateComparisonPeriod(from, to, filterValues.comparisonType)

  const currentPeriodFilters = transformFilterSelectionsToOrderItemsSchemaFilters(
    filterValues,
    currentPeriodDateRange
  )

  const lastPeriodFilters = transformFilterSelectionsToOrderItemsSchemaFilters(
    filterValues,
    lastPeriodDateRange
  )

  const schema = useSchema(InsightsPortalSchema.OrderItem)

  const primaryDatasetInput = {
    schema,
    filters: currentPeriodFilters,
    metric,
    groupBy,
    secondaryMetrics: primarySecondaryMetrics,
  }

  const comparisonDatasetInput = {
    schema,
    filters: lastPeriodFilters,
    metric,
    groupBy,
    secondaryMetrics: comparisonSecondaryMetrics,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.RowNumber,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, ...rest } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    ...rest,
  }
}

export const useOrderItemsMetricLabels = () =>
  useDomainMessages({
    AVG_ITEM_FOUND: 'insightsPortalDomain.orderItems.metrics.AVG_ITEM_FOUND',
    AVG_ITEM_FILLED: 'insightsPortalDomain.orderItems.metrics.AVG_ITEM_FILLED',
    LOST_GMV: 'insightsPortalDomain.orderItems.metrics.LOST_GMV',
    AVG_BASKET: 'insightsPortalDomain.orderItems.metrics.AVG_BASKET',
    AVG_ORDERED_ITEM: 'insightsPortalDomain.orderItems.metrics.AVG_ORDERED_ITEM',
    AVG_ITEM_FILLED_PRICE: 'insightsPortalDomain.orderItems.metrics.AVG_ITEM_FILLED_PRICE',
    COUNT_ORDER: 'insightsPortalDomain.orderItems.metrics.COUNT_ORDER',
  })

type UseCompareToBenchmarkForOrderItemsArg = {
  metric: OrderItemsMetrics
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  primaryFiltersOverride?: { [key: string]: SimpleOption[] }
  comparisonFiltersOverride?: { [key: string]: SimpleOption[] }
  groupBy: string
  comparisonGroupBy?: string
  // TODO: Make this mandatory prop. Making this optional with a default so it doesn't break other uses.
  benchmarkSchema?:
    | InsightsPortalSchema.OrderItem
    | InsightsPortalSchema.ProductByPartnerBenchmark
    | InsightsPortalSchema.ProductByCategoryBenchmark
  skip?: boolean
  comparisonMetric?:
    | ProductByCategoryBenchmarkMetrics
    | ProductByPartnerBenchmarkMetrics
    | OrderItemsMetrics
  primarySecondaryMetrics?: OrderItemsMetrics[]
  comparisonSecondaryMetrics?: Array<
    ProductByCategoryBenchmarkMetrics | ProductByPartnerBenchmarkMetrics | OrderItemsMetrics
  >
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

// TODO: convert this to a more generic usage of the grouped comparison api, does not have to specifically be for benchmarks
export const useCompareToBenchmarkForOrderItems = ({
  metric,
  groupBy,
  comparisonGroupBy = groupBy,
  orderBy,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  primaryFiltersOverride = null,
  comparisonFiltersOverride = null,
  skip = false,
  benchmarkSchema = InsightsPortalSchema.ProductByPartnerBenchmark,
  comparisonMetric,
  primarySecondaryMetrics,
  comparisonSecondaryMetrics,
}: UseCompareToBenchmarkForOrderItemsArg) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const primaryFilterValues = {
    ...selectedFiltersValues,
    ...filtersObjOverride,
    ...primaryFiltersOverride,
  }
  const comparisonFilterValues = {
    ...selectedFiltersValues,
    ...filtersObjOverride,
    ...comparisonFiltersOverride,
  }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const schema = useSchema(InsightsPortalSchema.OrderItem)

  const primaryFilters = transformFilterSelectionsToOrderItemsSchemaFilters(
    primaryFilterValues,
    currentPeriodDateRange
  )

  const comparisonFilters = transformFilterSelectionsToOrderItemsSchemaFilters(
    comparisonFilterValues,
    currentPeriodDateRange
  )

  const primaryDatasetInput = {
    schema,
    filters: primaryFilters,
    metric,
    groupBy,
    secondaryMetrics: primarySecondaryMetrics,
  }

  const comparisonDatasetInput = {
    schema: useSchema(benchmarkSchema),
    filters: comparisonFilters,
    metric: comparisonMetric || metric,
    groupBy: comparisonGroupBy,
    secondaryMetrics: comparisonSecondaryMetrics,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.GroupValue,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, ...rest } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    ...rest,
  }
}
